import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
} from "@fluentui/react-components";
import {
  CheckmarkCircleFilled,
  DismissRegular,
  DocumentCheckmarkRegular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "hooks/useAppTheme";
import { OfferAzureSearch } from "library/types/types";
import api from "service/api";
import { getInsurerShortName } from "library/utils/insurerDisplayName";
import { getSearchRowInvoicePayer } from "components/InvoicePayer";

// --- Types ---

/** Insurance line taxonomy codes understood by the BFF (see QTGCommonConstants.INSURANCE_LINE_*) */
export type InsuranceLine = "MOTORTPL" | "MOTORALLRISK";

/** One paid invoice line of an offer (MTPL or CASCO) */
interface OfferInvoiceLine {
  insuranceLine: InsuranceLine;
  invoiceNumber: string | null;
  invoiceDate: string; // yyyy-MM-dd
  premium: number;
  insurer: string | null;
  /** invoice period in months (InvoiceMtplPeriod / InvoiceCascoPeriod from the search row) */
  period: number | null;
  /** number of payments (InvoiceMtplPayments / InvoiceCascoPayments from the search row) */
  payments: number | null;
}

interface OfferWithPaidInvoices {
  offerId: string;
  clientName: string;
  vin: string;
  licensePlate: string | null;
  /** yyyy-MM-dd, InsuranceStartDate from the search row */
  insuranceStartDate: string | null;
  lines: OfferInvoiceLine[];
}

/** Response item of PATCH /invoices/getpotentialpolicies (BFF -> POL Azure Search) */
export interface PotentialPolicy {
  requestedVehicleIdentificationNumber: string;
  requestedInsuranceLine: string;
  policyID: number | null;
  policyVersionID: number | null;
  policyVersionLineID: number | null;
  policyName: string | null;
  policyNumber: string | null;
  internalPolicyNumber: number | null;
  subNumbers: string[] | null;
  clientID: number | null;
  clientName: string | null;
  isClientCompany: boolean | null;
  insurerID: number | null;
  insurerName: string | null;
  insuranceLineCode: string | null;
  premiumInclCommissionExclTaxes: number | null;
  currencyCode: string | null;
  validFrom: string | null;
  expiryDate: string | null;
  documentDate: string | null;
  policyVersionStatusCode: string | null;
  policyProcessStatusCode: string | null;
  serviceStatusCode: string | null;
  licensePlateNumber: string | null;
  vehicleIdentificationNumber: string | null;
  riskTariff: string | null;
  costCenterCode: string | null;
  isActive: boolean | null;
}

// --- Helpers ---

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "-";
  return dateStr.split("T")[0] ?? dateStr;
};

const formatAmount = (amount: number | null | undefined): string => {
  if (amount == null) return "-";
  return amount.toLocaleString("en", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const normalizeVin = (vin: string | null | undefined): string =>
  (vin || "").replace(/[\s-]/g, "").toUpperCase();

/** Lower-cased word tokens (letters/digits only), legal-form tokens removed: SIA "X" / X SIA / x -> ["x"] */
const clientNameTokens = (name: string | null | undefined): string[] =>
  (name || "")
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((w) => w.length > 0)
    .filter((w) => !["sia", "as", "aas", "ik", "zs", "ps", "ltd", "llc", "gmbh", "ooo", "uab", "ab", "oü", "oy"].includes(w));

/**
 * Same client when the word sets are equal or one is a subset of the other (both non-empty).
 * Word order is ignored (QTG "Bērziņš Jānis" vs GOS "Jānis Bērziņš").
 */
const clientNamesMatch = (a: string | null | undefined, b: string | null | undefined): boolean => {
  const ta = new Set(clientNameTokens(a));
  const tb = new Set(clientNameTokens(b));
  if (ta.size === 0 || tb.size === 0) return false;
  const [small, large] = ta.size <= tb.size ? [ta, tb] : [tb, ta];
  return Array.from(small).every((w) => large.has(w));
};

const lineLabel = (line: InsuranceLine): string =>
  line === "MOTORTPL" ? "MTPL" : "CASCO";

/** "InsuranceLine.MOTORTPL" / "MOTORTPL" -> "MTPL", "...MOTORALLRISK" -> "CASCO", anything else -> short code */
const insuranceLineCodeLabel = (code: string | null | undefined): string => {
  const short = taxonomyShort(code).toUpperCase();
  if (short === "MOTORTPL") return "MTPL";
  if (short === "MOTORALLRISK") return "CASCO";
  return short;
};

/** yyyy-MM-dd + months (same day of month, like POL validFrom/expiry) */
const addMonths = (dateStr: string | null | undefined, months: number): string | null => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().split("T")[0] ?? null;
};

/** Last segment of a taxonomy code, e.g. "PolicyVersionStatus.ISSUED" -> "ISSUED" */
const taxonomyShort = (code: string | null | undefined): string => {
  if (!code) return "-";
  const idx = code.lastIndexOf(".");
  return idx >= 0 ? code.substring(idx + 1) : code;
};

/**
 * Builds the offers with their paid invoice lines from the selected search rows.
 * Exported so the toolbar can compute eligibility with the same rule.
 */
export const buildOffersWithPaidInvoices = (
  items: OfferAzureSearch[],
): OfferWithPaidInvoices[] => {
  const ret: OfferWithPaidInvoices[] = [];
  for (const item of items) {
    if (!item?.VehicleVin) continue;
    const lines: OfferInvoiceLine[] = [];
    if (item.InvoiceMtplIsPaid === true && item.InvoiceMtplInvoiceIssuedAt) {
      lines.push({
        insuranceLine: "MOTORTPL",
        invoiceNumber: item.InvoiceMtplInvoiceNumber || null,
        invoiceDate: item.InvoiceMtplInvoiceIssuedAt.split("T")[0] ?? "",
        premium: item.InvoiceMtplPremium ?? 0,
        period: item.InvoiceMtplPeriod ?? null,
        payments: item.InvoiceMtplPayments ?? null,
        insurer: item.InvoiceMtplInsurer || null,
      });
    }
    if (item.InvoiceCascoIsPaid === true && item.InvoiceCascoInvoiceIssuedAt) {
      lines.push({
        insuranceLine: "MOTORALLRISK",
        invoiceNumber: item.InvoiceCascoInvoiceNumber || null,
        invoiceDate: item.InvoiceCascoInvoiceIssuedAt.split("T")[0] ?? "",
        premium: item.InvoiceCascoPremium ?? 0,
        period: item.InvoiceCascoPeriod ?? null,
        payments: item.InvoiceCascoPayments ?? null,
        insurer: item.InvoiceCascoInsurer || null,
      });
    }
    if (lines.length === 0) continue;
    ret.push({
      offerId: item.OfferId,
      clientName:
        item.ClientName || getSearchRowInvoicePayer(item, false).name || "",
      vin: item.VehicleVin,
      licensePlate: item.VehicleLicensePlate || null,
      insuranceStartDate: item.InsuranceStartDate
        ? (item.InsuranceStartDate.split("T")[0] ?? null)
        : null,
      lines,
    });
  }
  return ret;
};

// --- Component ---

type Props = {
  open: boolean;
  items: OfferAzureSearch[];
  /** Called when the dialog closes; hadResults is true when at least one mark-as-issued call was attempted */
  onClose: (hadResults: boolean) => void;
};

export const CheckPoliciesDialog = ({ open, items, onClose }: Props) => {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [policies, setPolicies] = useState<PotentialPolicy[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [checkedOffers, setCheckedOffers] = useState<Set<string>>(new Set());
  const [isMarking, setIsMarking] = useState(false);
  // Per-offer result: offerId -> null (success), "skipped", or error message
  const [markResults, setMarkResults] = useState<
    Record<string, string | null>
  >({});

  const offers = useMemo(() => buildOffersWithPaidInvoices(items), [items]);

  // Attach matching policies to each invoice line (by VIN + insurance line)
  const offersWithPolicies = useMemo(() => {
    return offers.map((offer) => ({
      ...offer,
      lines: offer.lines.map((line) => ({
        ...line,
        policies: policies.filter(
          (p) =>
            normalizeVin(p.requestedVehicleIdentificationNumber) ===
              normalizeVin(offer.vin) &&
            (p.requestedInsuranceLine || "").toUpperCase() ===
              line.insuranceLine,
        ),
      })),
    }));
  }, [offers, policies]);

  // Fetch potential policies when dialog opens
  useEffect(() => {
    if (!open) return;

    const fetchPolicies = async () => {
      setIsLoading(true);
      setError(null);
      setPolicies([]);
      setCheckedOffers(new Set());
      setMarkResults({});
      try {
        const payload = offers.flatMap((offer) =>
          offer.lines.map((line) => ({
            vehicleIdentificationNumber: offer.vin,
            invoiceDate: line.invoiceDate,
            insuranceLine: line.insuranceLine,
          })),
        );
        const response = await api.patch(
          "/invoices/getpotentialpolicies",
          payload,
        );
        const found: PotentialPolicy[] = response.data || [];
        setPolicies(found);

        // Auto-check offers where every paid invoice line has a policy in POL that matches
        // completely (client, insurer, premium and validity) - any mismatch leaves the offer unchecked
        const autoChecked = new Set<string>();
        for (const offer of offers) {
          const allLinesFullyMatched = offer.lines.every((line) =>
            found.some((p) => {
              const sameVehicleAndLine =
                normalizeVin(p.requestedVehicleIdentificationNumber) ===
                  normalizeVin(offer.vin) &&
                (p.requestedInsuranceLine || "").toUpperCase() ===
                  line.insuranceLine;
              if (!sameVehicleAndLine) return false;
              const cmp = comparePolicy(offer, line, p);
              return (
                cmp.clientMatches &&
                cmp.insurerMatches &&
                cmp.premiumMatches &&
                cmp.validityMatches
              );
            }),
          );
          if (allLinesFullyMatched) autoChecked.add(offer.offerId);
        }
        setCheckedOffers(autoChecked);
      } catch (err: any) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            t(
              "qtg.checkPolicies.fetchError",
              "Failed to fetch potential policies",
            ),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (isLoading || isMarking) return;
    const hadResults = Object.keys(markResults).length > 0;
    setPolicies([]);
    setError(null);
    setCheckedOffers(new Set());
    setMarkResults({});
    onClose(hadResults);
  };

  const toggleOffer = (offerId: string) => {
    setCheckedOffers((prev) => {
      const next = new Set(prev);
      if (next.has(offerId)) {
        next.delete(offerId);
      } else {
        next.add(offerId);
      }
      return next;
    });
  };

  /** Validity of the insurance the invoice was issued for: offer start date + invoice period (default 12 months) */
  const invoiceValidityRange = (
    offer: OfferWithPaidInvoices,
    line: OfferInvoiceLine,
  ): { from: string | null; to: string | null } => {
    const from = offer.insuranceStartDate;
    if (!from) return { from: null, to: null };
    return { from, to: addMonths(from, line.period || 12) };
  };

  const invoiceValidity = (offer: OfferWithPaidInvoices, line: OfferInvoiceLine): string => {
    const { from, to } = invoiceValidityRange(offer, line);
    if (!from) return "-";
    return `${from} - ${to ?? "-"}`;
  };

  /** Comparison of a POL policy line against the invoice line it was matched to */
  const comparePolicy = (offer: OfferWithPaidInvoices, line: OfferInvoiceLine, p: PotentialPolicy) => {
    const insurerMatches =
      !!line.insurer &&
      !!p.insurerName &&
      getInsurerShortName(line.insurer).toUpperCase() === getInsurerShortName(p.insurerName).toUpperCase();
    const premiumMatches =
      p.premiumInclCommissionExclTaxes != null &&
      Math.abs(p.premiumInclCommissionExclTaxes - line.premium) < 0.005;
    const { from, to } = invoiceValidityRange(offer, line);
    const validityMatches =
      !!from && !!to && formatDate(p.validFrom) === from && formatDate(p.expiryDate) === to;
    const clientMatches = clientNamesMatch(offer.clientName, p.clientName);
    return { insurerMatches, premiumMatches, validityMatches, clientMatches };
  };

  // Highlight colors for mismatches: red = mismatch, orange = premium mismatch on an invoice with more than one payment
  const mismatchRed: React.CSSProperties = { color: theme.semanticColors.errorText, fontWeight: 600 };
  const mismatchOrange: React.CSSProperties = { color: theme.semanticColors.severeWarningIcon, fontWeight: 600 };

  const handleMarkAsIssued = async () => {
    setIsMarking(true);
    setError(null);
    const resultMap: Record<string, string | null> = {};
    for (const offer of offers) {
      if (!checkedOffers.has(offer.offerId)) {
        resultMap[offer.offerId] = "skipped";
      }
    }
    // Sequential calls: the endpoint handles one offer at a time
    for (const offer of offers) {
      if (!checkedOffers.has(offer.offerId)) continue;
      try {
        // the endpoint matches the invoice by number; the offer holds one paid invoice
        const invoiceNumber = offer.lines.find((l) => l.invoiceNumber)?.invoiceNumber;
        if (!invoiceNumber) {
          resultMap[offer.offerId] = t(
            "qtg.checkPolicies.noInvoiceId",
            "Paid invoice not found for the offer",
          );
          continue;
        }
        await api.patch(
          `/invoices/markasissued/${offer.offerId}/${encodeURIComponent(invoiceNumber)}`,
        );
        resultMap[offer.offerId] = null;
      } catch (err: any) {
        resultMap[offer.offerId] =
          err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.message ||
          t("qtg.checkPolicies.markError", "Failed to mark invoice as issued");
      }
      setMarkResults({ ...resultMap });
    }
    setMarkResults(resultMap);
    setIsMarking(false);
  };

  const hasResults = Object.keys(markResults).length > 0;

  const thStyle: React.CSSProperties = {
    padding: "6px 8px",
    textAlign: "left",
    fontWeight: 600,
    color: theme.semanticColors.bodySubtext,
    borderBottom: `2px solid ${theme.semanticColors.bodyDivider}`,
    position: "sticky",
    top: 0,
    background: theme.semanticColors.bodyFrameBackground,
    zIndex: 1,
  };

  const tdStyle: React.CSSProperties = {
    padding: "4px 8px",
    borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
    verticalAlign: "middle",
  };

  const ellipsis: React.CSSProperties = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const COLS = 10;

  return (
    <Dialog open={open} onOpenChange={(e, data) => !data.open && handleClose()}>
      <DialogSurface style={{ maxWidth: "95vw", width: "1200px" }}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={handleClose}
                disabled={isLoading || isMarking}
              />
            }
          >
            {t("qtg.checkPolicies.dialog.title", "Check policies")}
          </DialogTitle>
          <DialogContent>
            {isLoading && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "48px 0",
                }}
              >
                <Spinner
                  size="large"
                  label={t("qtg.common.processing", "Processing...")}
                />
              </div>
            )}

            {error && (
              <div
                style={{
                  color: theme.semanticColors.errorText,
                  padding: "8px 0",
                }}
              >
                {error}
              </div>
            )}

            {!isLoading && policies.length > 0 && (
              <div style={{ maxHeight: "60vh", overflow: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                    color: theme.semanticColors.bodyText,
                    tableLayout: "fixed",
                  }}
                >
                  <colgroup>
                    <col style={{ width: 32 }} />
                    <col style={{ width: 130 }} />
                    <col />
                    <col style={{ width: 150 }} />
                    <col style={{ width: 70 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 180 }} />
                    <col style={{ width: 90 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={thStyle}></th>
                      <th style={thStyle}>
                        {t("qtg.potentialPolicies.offerOrPolicy", "Offer / Policy")}
                      </th>
                      <th style={thStyle}>
                        {t("qtg.potentialPolicies.client", "Client")}
                      </th>
                      <th style={thStyle}>
                        {t("qtg.potentialPolicies.insurer", "Insurer")}
                      </th>
                      <th style={thStyle}>
                        {t("qtg.potentialPolicies.line", "Line")}
                      </th>
                      <th style={thStyle}>
                        {t("qtg.potentialPolicies.number", "Invoice # / Policy name")}
                      </th>
                      <th style={{ ...thStyle, textAlign: "right" }}>
                        {t("qtg.potentialPolicies.premium", "Premium")}
                      </th>
                      <th style={thStyle}>
                        {t("qtg.potentialPolicies.date", "Invoice / Document date")}
                      </th>
                      <th style={thStyle}>
                        {t("qtg.potentialPolicies.validity", "Valid from - to")}
                      </th>
                      <th style={thStyle}>
                        {t("qtg.potentialPolicies.status", "Status")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {offersWithPolicies.map((offer, idx) => (
                      <React.Fragment key={offer.offerId}>
                        {idx > 0 && (
                          <tr>
                            <td colSpan={COLS} style={{ height: 8, border: "none" }} />
                          </tr>
                        )}
                        {/* Offer row */}
                        <tr
                          style={{
                            background: theme.semanticColors.bodyStandoutBackground,
                          }}
                        >
                          <td style={tdStyle}>
                            <Checkbox
                              checked={checkedOffers.has(offer.offerId)}
                              onChange={() => toggleOffer(offer.offerId)}
                              disabled={isMarking || hasResults}
                            />
                          </td>
                          <td style={{ ...tdStyle, fontWeight: 600 }}>{offer.offerId}</td>
                          <td style={{ ...tdStyle, ...ellipsis }} title={offer.clientName}>
                            {offer.clientName}
                          </td>
                          <td style={tdStyle}></td>
                          <td style={tdStyle}></td>
                          <td
                            style={{ ...tdStyle, ...ellipsis }}
                            title={`${offer.vin}${offer.licensePlate ? ` / ${offer.licensePlate}` : ""}`}
                          >
                            {offer.vin}
                            {offer.licensePlate ? ` / ${offer.licensePlate}` : ""}
                          </td>
                          <td style={tdStyle} colSpan={4}></td>
                        </tr>
                        {/* Invoice line rows with their matched policies */}
                        {offer.lines.map((line) => (
                          <React.Fragment key={`${offer.offerId}-${line.insuranceLine}`}>
                            <tr>
                              <td style={tdStyle}></td>
                              <td style={{ ...tdStyle, color: theme.semanticColors.bodySubtext }}>
                                {t("qtg.potentialPolicies.invoice", "Invoice")}
                              </td>
                              <td style={tdStyle}></td>
                              <td style={tdStyle}>
                                {line.insurer ? (
                                  <Badge appearance="outline" size="small">
                                    {line.insurer}
                                  </Badge>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td style={tdStyle}>
                                <Badge appearance="tint" size="small">
                                  {lineLabel(line.insuranceLine)}
                                </Badge>
                              </td>
                              <td style={tdStyle}>{line.invoiceNumber || "-"}</td>
                              <td
                                style={{
                                  ...tdStyle,
                                  textAlign: "right",
                                  fontWeight: 600,
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {formatAmount(line.premium)}
                              </td>
                              <td style={tdStyle}>{line.invoiceDate}</td>
                              <td style={tdStyle}>
                                {invoiceValidity(offer, line)}
                              </td>
                              <td style={tdStyle}></td>
                            </tr>
                            {line.policies.map((p) => {
                              const cmp = comparePolicy(offer, line, p);
                              const premiumStyle = cmp.premiumMatches
                                ? undefined
                                : (line.payments ?? 1) > 1
                                  ? mismatchOrange
                                  : mismatchRed;
                              return (
                              <tr key={`${p.policyVersionLineID ?? p.policyVersionID}-${p.policyNumber}`}>
                                <td style={tdStyle}></td>
                                <td
                                  style={{ ...tdStyle, ...ellipsis, fontWeight: 600 }}
                                  title={p.policyNumber || ""}
                                >
                                  {p.policyNumber || "-"}
                                </td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    ...ellipsis,
                                    color: theme.semanticColors.bodySubtext,
                                    ...(cmp.clientMatches ? {} : mismatchRed),
                                  }}
                                  title={
                                    cmp.clientMatches
                                      ? p.clientName || ""
                                      : t("qtg.potentialPolicies.clientMismatch", "Policy client differs from the offer client: {{client}}", { client: p.clientName || "-" })
                                  }
                                >
                                  {p.clientName || "-"}
                                </td>
                                <td style={{ ...tdStyle, ...ellipsis }} title={p.insurerName || ""}>
                                  {p.insurerName ? (
                                    <Badge
                                      appearance="outline"
                                      size="small"
                                      color={cmp.insurerMatches ? undefined : "danger"}
                                      style={cmp.insurerMatches ? undefined : mismatchRed}
                                    >
                                      {getInsurerShortName(p.insurerName)}
                                    </Badge>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td style={tdStyle}>
                                  <Badge appearance="tint" size="small">
                                    {insuranceLineCodeLabel(p.insuranceLineCode)}
                                  </Badge>
                                </td>
                                <td style={{ ...tdStyle, ...ellipsis }} title={p.policyName || ""}>
                                  {p.policyName || "-"}
                                </td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    textAlign: "right",
                                    fontVariantNumeric: "tabular-nums",
                                    ...premiumStyle,
                                  }}
                                  title={
                                    cmp.premiumMatches
                                      ? undefined
                                      : t(
                                          "qtg.potentialPolicies.premiumMismatch",
                                          "Policy premium differs from the invoice premium ({{payments}} payment(s))",
                                          { payments: line.payments ?? 1 },
                                        )
                                  }
                                >
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                    {cmp.premiumMatches && (
                                      <CheckmarkCircleFilled
                                        style={{ color: theme.semanticColors.successIcon, fontSize: 16 }}
                                      />
                                    )}
                                    {formatAmount(p.premiumInclCommissionExclTaxes)}
                                  </span>
                                </td>
                                <td style={tdStyle}>{formatDate(p.documentDate)}</td>
                                <td
                                  style={{ ...tdStyle, ...(cmp.validityMatches ? {} : mismatchRed) }}
                                  title={
                                    cmp.validityMatches
                                      ? undefined
                                      : t(
                                          "qtg.potentialPolicies.validityMismatch",
                                          "Policy validity differs from the invoice validity",
                                        )
                                  }
                                >
                                  {formatDate(p.validFrom)} - {formatDate(p.expiryDate)}
                                </td>
                                <td style={tdStyle}>
                                  <Badge
                                    appearance="filled"
                                    color={
                                      taxonomyShort(p.policyVersionStatusCode) === "ISSUED"
                                        ? "success"
                                        : "warning"
                                    }
                                    size="small"
                                  >
                                    {taxonomyShort(p.policyVersionStatusCode)}
                                  </Badge>
                                </td>
                              </tr>
                              );
                            })}
                            {line.policies.length === 0 && (
                              <tr>
                                <td style={tdStyle}></td>
                                <td
                                  colSpan={COLS - 1}
                                  style={{
                                    ...tdStyle,
                                    color: theme.semanticColors.bodySubtext,
                                    fontStyle: "italic",
                                  }}
                                >
                                  {t(
                                    "qtg.potentialPolicies.noMatchingPolicies",
                                    "No matching policies found in POL",
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                        {/* Per-offer mark result */}
                        {offer.offerId in markResults && (
                          <tr>
                            <td style={tdStyle}></td>
                            <td
                              colSpan={COLS - 1}
                              style={{
                                ...tdStyle,
                                padding: "8px",
                                background:
                                  markResults[offer.offerId] === "skipped"
                                    ? theme.semanticColors.bodyStandoutBackground
                                    : markResults[offer.offerId]
                                      ? theme.semanticColors.errorBackground
                                      : theme.semanticColors.successBackground,
                                color:
                                  markResults[offer.offerId] === "skipped"
                                    ? theme.semanticColors.bodySubtext
                                    : markResults[offer.offerId]
                                      ? theme.semanticColors.errorText
                                      : theme.semanticColors.successText,
                                borderRadius: 2,
                                fontStyle:
                                  markResults[offer.offerId] === "skipped" ? "italic" : undefined,
                              }}
                            >
                              {markResults[offer.offerId] === "skipped"
                                ? t("qtg.checkPolicies.skipped", "Skipped — not selected")
                                : markResults[offer.offerId]
                                  ? markResults[offer.offerId]
                                  : t(
                                      "qtg.checkPolicies.success",
                                      "Invoice marked as issued successfully",
                                    )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && policies.length === 0 && !error && (
              <div
                style={{
                  padding: "32px 0",
                  textAlign: "center",
                  color: theme.semanticColors.bodySubtext,
                }}
              >
                {t("qtg.potentialPolicies.noPolicies", "No potential policies found in POL")}
              </div>
            )}
          </DialogContent>

          <DialogActions>
            {!hasResults && !isLoading && policies.length > 0 && (
              <Button
                appearance="primary"
                icon={isMarking ? <Spinner size="tiny" /> : <DocumentCheckmarkRegular />}
                disabled={checkedOffers.size === 0 || isMarking}
                onClick={handleMarkAsIssued}
              >
                {isMarking
                  ? t("qtg.common.processing", "Processing...")
                  : t("qtg.checkPolicies.dialog.confirm", "Mark invoice(s) as issued")}
                {!isMarking && checkedOffers.size > 0 && ` (${checkedOffers.size})`}
              </Button>
            )}
            {hasResults && (
              <Button appearance="primary" onClick={handleClose} disabled={isMarking}>
                {t("qtg.common.close", "Close")}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
